import { useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Modal } from '../ui/Modal';
import { PALETTE, type Team } from '@/types';
import { nanoid } from 'nanoid';
import { Trash2, Search, Building2, X, UserPlus, Users, Filter } from 'lucide-react';
import { cn } from '@/utils/cn';
import { hashHue, initialsOf } from '@/utils/colors';

interface Props { id: string | null; onClose: () => void; }

export function TeamEditor({ id, onClose }: Props) {
  const { teams, branches, employees, saveTeam, deleteTeam, setModal } = useStore();
  const existing = id ? teams.find((t) => t.id === id) : null;
  const [draft, setDraft] = useState<Team>(existing ?? {
    id: nanoid(), name: '', color: PALETTE[teams.length % PALETTE.length], member_ids: [], is_archived: false,
    branch_id: branches[0]?.id ?? null,
    icon: '', description: '', group: '', order: teams.length + 1,
  });

  // Группы из существующих команд — для подсказки
  const groupOptions = useMemo(
    () => Array.from(new Set(teams.map((t) => t.group?.trim()).filter((g): g is string => !!g))).sort(),
    [teams],
  );

  // Поиск + фильтр филиала в составе
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'team-branch' | 'all'>('team-branch');
  const [filterBranchId, setFilterBranchId] = useState<string | 'all'>('all');

  const valid = draft.name.trim().length > 0;
  const branchById = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);

  // Видимые в текущем фильтре сотрудники
  const visibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      .filter((e) => {
        // Базовое ограничение по выбранному режиму
        if (scope === 'team-branch' && draft.branch_id && e.branch_id !== draft.branch_id) return false;
        if (scope === 'all' && filterBranchId !== 'all' && e.branch_id !== filterBranchId) return false;
        return true;
      })
      .filter((e) => !q ||
        e.full_name.toLowerCase().includes(q) ||
        (e.role ?? '').toLowerCase().includes(q) ||
        (branchById.get(e.branch_id ?? '')?.name ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [employees, scope, filterBranchId, draft.branch_id, search, branchById]);

  // Выбранные сотрудники (по member_ids) — могут быть из других филиалов
  const selectedEmployees = useMemo(() => {
    return draft.member_ids
      .map((mid) => employees.find((e) => e.id === mid))
      .filter((e): e is NonNullable<typeof e> => !!e)
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [draft.member_ids, employees]);

  const toggleMember = (id: string) => {
    setDraft({
      ...draft,
      member_ids: draft.member_ids.includes(id)
        ? draft.member_ids.filter((x) => x !== id)
        : [...draft.member_ids, id],
    });
  };

  const addAllVisible = () => {
    const next = new Set(draft.member_ids);
    for (const e of visibleEmployees) next.add(e.id);
    setDraft({ ...draft, member_ids: Array.from(next) });
  };
  const removeAllVisible = () => {
    const visibleIds = new Set(visibleEmployees.map((e) => e.id));
    setDraft({ ...draft, member_ids: draft.member_ids.filter((mid) => !visibleIds.has(mid)) });
  };

  const allVisibleSelected = visibleEmployees.length > 0 && visibleEmployees.every((e) => draft.member_ids.includes(e.id));

  return (
    <Modal open onClose={onClose} title={existing ? 'Команда' : 'Новая команда'} size="lg"
      footer={<>
        {existing && <button className="btn-ghost text-status-refused mr-auto"
          onClick={() => { if (confirm('Удалить команду?')) { void deleteTeam(existing.id); onClose(); } }}>
          <Trash2 className="w-4 h-4" /> Удалить
        </button>}
        <button className="btn-ghost" onClick={onClose}>Отмена</button>
        <button className="btn-primary" disabled={!valid} onClick={async () => { await saveTeam(draft); onClose(); }}>Сохранить</button>
      </>}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Название*</label>
            <input className="input" autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Филиал «прописки»</label>
            <select className="input" value={draft.branch_id ?? ''} onChange={(e) => setDraft({ ...draft, branch_id: e.target.value || null })}>
              <option value="">— любой —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-[80px_80px_1fr] gap-3">
          <div>
            <label className="field-label">Порядок</label>
            <input type="number" className="input text-center tabular-nums" value={draft.order ?? ''}
              onChange={(e) => setDraft({ ...draft, order: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </div>
          <div>
            <label className="field-label">Иконка</label>
            <input className="input text-center text-lg" maxLength={4} placeholder="🎭" value={draft.icon ?? ''}
              onChange={(e) => setDraft({ ...draft, icon: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Группа</label>
            <input className="input" list="team-groups" placeholder="напр. Выездные" value={draft.group ?? ''}
              onChange={(e) => setDraft({ ...draft, group: e.target.value })} />
            <datalist id="team-groups">
              {groupOptions.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
        </div>

        <div>
          <label className="field-label">Описание</label>
          <textarea className="input min-h-[64px] py-2 resize-y" placeholder="Короткое описание команды"
            value={draft.description ?? ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>

        <div>
          <label className="field-label">Цвет</label>
          <div className="flex flex-wrap gap-1.5">
            {PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => setDraft({ ...draft, color: c })}
                className={cn('w-7 h-7 rounded-full', draft.color.toLowerCase() === c.toLowerCase() && 'ring-2 ring-ink-1 ring-offset-2')}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>

        {/* Выбранные участники (горизонтальные чипы) */}
        <div>
          <label className="field-label flex items-center justify-between">
            <span>Состав ({draft.member_ids.length})</span>
            {draft.member_ids.length > 0 && (
              <button type="button" className="text-[11px] text-ink-3 hover:text-status-refused"
                onClick={() => setDraft({ ...draft, member_ids: [] })}>
                Очистить
              </button>
            )}
          </label>
          {selectedEmployees.length === 0 ? (
            <div className="text-xs text-ink-3 italic py-2">Никто не выбран. Используйте поиск ниже.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto p-2 rounded-lg bg-surface-2/40 border border-line">
              {selectedEmployees.map((e) => {
                const b = e.branch_id ? branchById.get(e.branch_id) : null;
                return (
                  <span key={e.id} className="inline-flex items-center gap-1.5 h-7 pl-1 pr-2 rounded-full bg-surface-1 border border-line text-xs">
                    <span className="w-5 h-5 rounded-full grid place-items-center text-[9px] font-bold text-white"
                      style={{ backgroundColor: `hsl(${hashHue(e.full_name)}, 55%, 48%)` }}>
                      {initialsOf(e.full_name)}
                    </span>
                    <span className="truncate max-w-[140px]">{e.full_name}</span>
                    {b && (
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: b.color }} title={b.name} />
                    )}
                    <button type="button" onClick={() => toggleMember(e.id)} className="text-ink-3 hover:text-status-refused -mr-1 ml-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Поиск по сотрудникам */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
              <input
                className="input pl-8 h-9"
                placeholder="Поиск по имени, роли, филиалу…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-1 bg-surface-2 rounded-full p-1">
              <button type="button"
                onClick={() => setScope('team-branch')}
                className={cn('h-7 px-3 rounded-full text-xs font-medium transition-all flex items-center gap-1.5',
                  scope === 'team-branch' ? 'bg-accent text-white shadow-soft' : 'text-ink-2 hover:bg-surface-3')}
                title="Только сотрудники филиала команды"
              >
                <Building2 className="w-3.5 h-3.5" /> Свой филиал
              </button>
              <button type="button"
                onClick={() => setScope('all')}
                className={cn('h-7 px-3 rounded-full text-xs font-medium transition-all flex items-center gap-1.5',
                  scope === 'all' ? 'bg-accent text-white shadow-soft' : 'text-ink-2 hover:bg-surface-3')}
                title="Все сотрудники всех филиалов"
              >
                <Users className="w-3.5 h-3.5" /> Все филиалы
              </button>
            </div>

            {scope === 'all' && (
              <select
                className="input h-9 w-[180px]"
                value={filterBranchId}
                onChange={(e) => setFilterBranchId(e.target.value as string | 'all')}
              >
                <option value="all">Все филиалы</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}

            <button type="button" className="btn-ghost h-9"
              onClick={() => setModal({ kind: 'employee', id: null })}
              title="Создать нового сотрудника">
              <UserPlus className="w-4 h-4" /> Новый
            </button>
          </div>

          <div className="border border-line rounded-lg bg-surface-1 overflow-hidden">
            <div className="flex items-center justify-between px-3 h-8 border-b border-line bg-surface-2/50 text-[11px] text-ink-3">
              <span className="flex items-center gap-1.5">
                <Filter className="w-3 h-3" />
                Найдено: <b className="text-ink-2">{visibleEmployees.length}</b>
              </span>
              {visibleEmployees.length > 0 && (
                <button type="button"
                  onClick={allVisibleSelected ? removeAllVisible : addAllVisible}
                  className="text-accent hover:underline font-semibold">
                  {allVisibleSelected ? 'Снять все' : 'Выбрать все видимые'}
                </button>
              )}
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {visibleEmployees.length === 0 ? (
                <div className="text-xs text-ink-3 italic text-center py-6">
                  Никого не найдено. Попробуйте сменить фильтр.
                </div>
              ) : (
                <div className="divide-y divide-line/60">
                  {visibleEmployees.map((emp) => {
                    const checked = draft.member_ids.includes(emp.id);
                    const b = emp.branch_id ? branchById.get(emp.branch_id) : null;
                    return (
                      <label key={emp.id}
                        className={cn('flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors',
                          checked ? 'bg-accent-soft/40' : 'hover:bg-surface-2')}>
                        <input type="checkbox" checked={checked} onChange={() => toggleMember(emp.id)} className="w-4 h-4 accent-accent flex-shrink-0" />
                        <span className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: `hsl(${hashHue(emp.full_name)}, 55%, 48%)` }}>
                          {initialsOf(emp.full_name)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink-1 truncate">{emp.full_name}</div>
                          {((emp.position || emp.role) || b) && (
                            <div className="text-[10px] text-ink-3 flex items-center gap-1.5 truncate">
                              {(emp.position || emp.role) && <span>{emp.position || emp.role}</span>}
                              {(emp.position || emp.role) && b && <span>·</span>}
                              {b && (
                                <span className="flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: b.color }} />
                                  {b.name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Галочка «В архиве» убрана по просьбе заказчика (10.07.2026): её ставили по ошибке,
            а архивная команда пропадает из всех выпадающих списков (карточка сделки, создание
            игры) — люди не понимали, куда делась только что созданная команда. Поле is_archived
            в данных осталось, скрыт только переключатель. Вернуть — раскомментировать.
        <label className="flex items-center gap-2 text-sm cursor-pointer text-ink-2">
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={draft.is_archived} onChange={(e) => setDraft({ ...draft, is_archived: e.target.checked })} />
          В архиве
        </label>
        */}
      </div>
    </Modal>
  );
}
